import React from 'react';
import { TrendingUp } from 'lucide-react';
import { seededWalk, toLineD, toAreaD } from './mockChart';

// Portfolio value line: deterministic organic walk, overall upward trend.
const PORTFOLIO_PTS  = seededWalk({ seed: 1337, points: 48, width: 800, yStart: 200, yEnd: 58, vol: 9, band: 16 });
const PORTFOLIO_LINE = toLineD(PORTFOLIO_PTS);
const PORTFOLIO_AREA = toAreaD(PORTFOLIO_PTS, 240);

// Chart axis labels (static).
const Y_TICKS = ['$90k', '$70k', '$50k', '$30k'];
const X_TICKS = ['Apr', 'May', 'Jun', 'Jul'];

const STATS: { label: string; value: string; tone?: 'green' | 'default' }[] = [
  { label: 'TOTAL VALUE',  value: '$83,078' },
  { label: 'ALL-TIME P&L', value: '+$33,718', tone: 'green' },
  { label: '24H CHANGE',   value: '+1.9%',    tone: 'green' },
  { label: 'ASSETS',       value: '4' },
];

const TABS = ['Portfolio', 'Transactions', 'Strategy', 'Tax', 'Market'];

// Static asset-allocation slices (by value) for the donut + legend.
const ALLOCATION: { symbol: string; pct: number; color: string }[] = [
  { symbol: 'BTC',  pct: 35, color: '#f7931a' },
  { symbol: 'ETH',  pct: 33, color: '#627eea' },
  { symbol: 'NVDA', pct: 18, color: '#76b900' }, // NVIDIA brand green
  { symbol: 'XRP',  pct: 14, color: '#00a5df' },
];

// Static holdings (crypto + ETF + stock). Figures are internally consistent:
// value = amount x current; P&L = (current - avg) x amount.
const HOLDINGS: {
  name: string; symbol: string; amount: string; avg: string; current: string;
  chg: string; chgUp: boolean; value: string; pnl: string; pnlUp: boolean;
  wallet: string; color: string;
}[] = [
  { name: 'XRP',      symbol: 'XRP',  amount: '5,000', avg: '$0.52',   current: '$2.35',   chg: '+4.2%', chgUp: true,  value: '$11,750', pnl: '+$9,150',  pnlUp: true, wallet: 'Ledger',   color: '#00a5df' },
  { name: 'Bitcoin',  symbol: 'BTC',  amount: '0.45',  avg: '$38,000', current: '$64,200', chg: '+1.8%', chgUp: true,  value: '$28,890', pnl: '+$11,790', pnlUp: true, wallet: 'Trezor',   color: '#f7931a' },
  { name: 'Ethereum', symbol: 'ETH',  amount: '8.50',  avg: '$2,240',  current: '$3,180',  chg: '-0.9%', chgUp: false, value: '$27,030', pnl: '+$7,990',  pnlUp: true, wallet: 'MetaMask', color: '#627eea' },
  { name: 'NVIDIA',   symbol: 'NVDA', amount: '120',   avg: '$88.50',  current: '$128.40', chg: '+2.6%', chgUp: true,  value: '$15,408', pnl: '+$4,788',  pnlUp: true, wallet: 'Stake',    color: '#76b900' },
];

// Donut geometry: stacked SVG circles, each drawn as one arc via stroke-dasharray.
const DONUT_R = 60;
const DONUT_C = 2 * Math.PI * DONUT_R; // circumference
const DONUT_GAP = 3;                   // px gap between slices for a padded look

const GREEN = '#6db981';
const RED   = '#c4615e';

function WalletTag({ label }: { label: string }) {
  return (
    <span
      className="inline-block text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{
        backgroundColor: 'rgba(255,255,255,0.05)',
        border: '0.5px solid rgba(255,255,255,0.08)',
        color: 'rgba(244,241,234,0.6)',
      }}
    >
      {label}
    </span>
  );
}

export function AppPreview() {
  return (
    <section className="px-6 pb-20">
      <div className="max-w-5xl mx-auto">
        <div
          className="rounded-xl p-2 sm:p-3"
          style={{
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.01))',
            border: '0.5px solid rgba(255,255,255,0.08)',
          }}
        >
          <div
            className="rounded-lg"
            style={{
              backgroundColor: '#15171c',
              padding: '20px',
              border: '0.5px solid rgba(255,255,255,0.05)',
            }}
          >
            {/* Header row */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4" style={{ color: '#f4f1ea' }} />
                <span className="text-[12px] font-medium" style={{ color: '#f4f1ea' }}>
                  MyCryptoPlanner
                </span>
              </div>
              <span className="text-[11px]" style={{ color: 'rgba(244,241,234,0.4)' }}>
                USD · Refresh
              </span>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {STATS.map((s) => (
                <div
                  key={s.label}
                  className="rounded-lg p-3"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    border: '0.5px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <div
                    className="text-[9px] tracking-wider"
                    style={{ color: 'rgba(244,241,234,0.4)' }}
                  >
                    {s.label}
                  </div>
                  <div
                    className="mt-1 text-[18px] font-semibold tabular-nums"
                    style={{ color: s.tone === 'green' ? GREEN : '#f4f1ea' }}
                  >
                    {s.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Tab row */}
            <div
              className="mt-5 flex gap-1 p-1 rounded-lg overflow-x-auto"
              style={{
                backgroundColor: 'rgba(255,255,255,0.02)',
                border: '0.5px solid rgba(255,255,255,0.05)',
              }}
            >
              {TABS.map((tab, i) => (
                <div
                  key={tab}
                  className="text-[11px] font-medium px-3 py-1.5 rounded-md whitespace-nowrap"
                  style={
                    i === 0
                      ? {
                          backgroundColor: 'rgba(255,255,255,0.06)',
                          color: '#f4f1ea',
                          border: '0.5px solid rgba(255,255,255,0.08)',
                        }
                      : { color: 'rgba(244,241,234,0.5)' }
                  }
                >
                  {tab}
                </div>
              ))}
            </div>

            {/* Portfolio body: value chart + asset allocation */}
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Value chart */}
              <div
                className="lg:col-span-3 rounded-lg p-3"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  border: '0.5px solid rgba(255,255,255,0.05)',
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div
                    className="text-[10px] tracking-wider"
                    style={{ color: 'rgba(244,241,234,0.4)' }}
                  >
                    PORTFOLIO VALUE · 90D
                  </div>
                  <div className="text-right leading-tight">
                    <div className="text-[13px] font-semibold tabular-nums" style={{ color: '#f4f1ea' }}>
                      $83,078
                    </div>
                    <div className="text-[10px] tabular-nums" style={{ color: GREEN }}>
                      +$33,718 (+68.3%)
                    </div>
                  </div>
                </div>

                {/* Plot with $ y-axis + date x-axis */}
                <div className="flex gap-2">
                  <div
                    className="flex flex-col justify-between text-[9px] tabular-nums py-0.5 flex-shrink-0"
                    style={{ color: 'rgba(244,241,234,0.35)' }}
                  >
                    {Y_TICKS.map((t) => <span key={t}>{t}</span>)}
                  </div>
                  <div className="flex-1">
                    <svg
                      viewBox="0 0 800 240"
                      preserveAspectRatio="none"
                      className="w-full h-[150px] sm:h-[172px]"
                    >
                      <defs>
                        <linearGradient id="lpFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%"  stopColor="#d4b483" stopOpacity="0.35" />
                          <stop offset="100%" stopColor="#d4b483" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <line x1="0" y1="60"  x2="800" y2="60"  stroke="rgba(255,255,255,0.04)" />
                      <line x1="0" y1="120" x2="800" y2="120" stroke="rgba(255,255,255,0.04)" />
                      <line x1="0" y1="180" x2="800" y2="180" stroke="rgba(255,255,255,0.04)" />
                      <path d={PORTFOLIO_AREA} fill="url(#lpFill)" />
                      <path
                        d={PORTFOLIO_LINE}
                        fill="none"
                        stroke="#d4b483"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div
                      className="flex justify-between text-[9px] mt-1"
                      style={{ color: 'rgba(244,241,234,0.35)' }}
                    >
                      {X_TICKS.map((t) => <span key={t}>{t}</span>)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Asset allocation donut + legend */}
              <div
                className="lg:col-span-2 rounded-lg p-3"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  border: '0.5px solid rgba(255,255,255,0.05)',
                }}
              >
                <div
                  className="text-[10px] tracking-wider mb-2"
                  style={{ color: 'rgba(244,241,234,0.4)' }}
                >
                  ASSET ALLOCATION · BY COIN
                </div>
                <div className="flex items-center gap-4">
                  <svg viewBox="0 0 160 160" className="w-[96px] h-[96px] flex-shrink-0">
                    <g transform="rotate(-90 80 80)">
                      {ALLOCATION.map((a, i) => {
                        const len   = (a.pct / 100) * DONUT_C;
                        const dash  = Math.max(len - DONUT_GAP, 0);
                        const start = ALLOCATION
                          .slice(0, i)
                          .reduce((s, x) => s + (x.pct / 100) * DONUT_C, 0);
                        return (
                          <circle
                            key={a.symbol}
                            cx="80" cy="80" r={DONUT_R}
                            fill="none"
                            stroke={a.color}
                            strokeWidth="22"
                            strokeDasharray={`${dash} ${DONUT_C - dash}`}
                            strokeDashoffset={-start}
                          />
                        );
                      })}
                    </g>
                    <text
                      x="80" y="84" textAnchor="middle"
                      className="tabular-nums"
                      fontSize="17" fontWeight="600" fill="#f4f1ea"
                    >
                      $83k
                    </text>
                  </svg>

                  <div className="flex-1 space-y-2">
                    {ALLOCATION.map((a) => (
                      <div key={a.symbol} className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: a.color }}
                        />
                        <span
                          className="text-[11px] font-medium w-9 flex-shrink-0"
                          style={{ color: 'rgba(244,241,234,0.75)' }}
                        >
                          {a.symbol}
                        </span>
                        <span className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                          <span
                            className="block h-full rounded-full"
                            style={{ width: `${a.pct}%`, backgroundColor: a.color }}
                          />
                        </span>
                        <span
                          className="text-[11px] tabular-nums w-8 text-right flex-shrink-0"
                          style={{ color: 'rgba(244,241,234,0.5)' }}
                        >
                          {a.pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Holdings - desktop table */}
            <div className="mt-4 hidden md:block">
              <table className="w-full text-[11px] tabular-nums" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: 'rgba(244,241,234,0.4)' }} className="text-[9px] tracking-wider">
                    <th className="text-left font-medium pb-2">ASSET</th>
                    <th className="text-right font-medium pb-2">AMOUNT</th>
                    <th className="text-right font-medium pb-2">AVG BUY</th>
                    <th className="text-right font-medium pb-2">CURRENT</th>
                    <th className="text-right font-medium pb-2">24H</th>
                    <th className="text-right font-medium pb-2">VALUE</th>
                    <th className="text-right font-medium pb-2">P&amp;L</th>
                    <th className="text-right font-medium pb-2">WALLET</th>
                  </tr>
                </thead>
                <tbody>
                  {HOLDINGS.map((h) => (
                    <tr key={h.symbol} style={{ borderTop: '0.5px solid rgba(255,255,255,0.05)' }}>
                      <td className="py-2.5">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0"
                            style={{ backgroundColor: h.color + '22', color: h.color }}
                          >
                            {h.symbol.slice(0, 2)}
                          </span>
                          <span className="font-medium" style={{ color: '#f4f1ea' }}>{h.name}</span>
                          <span style={{ color: 'rgba(244,241,234,0.4)' }}>{h.symbol}</span>
                        </div>
                      </td>
                      <td className="text-right" style={{ color: 'rgba(244,241,234,0.7)' }}>{h.amount}</td>
                      <td className="text-right" style={{ color: 'rgba(244,241,234,0.5)' }}>{h.avg}</td>
                      <td className="text-right" style={{ color: 'rgba(244,241,234,0.85)' }}>{h.current}</td>
                      <td className="text-right" style={{ color: h.chgUp ? GREEN : RED }}>{h.chg}</td>
                      <td className="text-right font-medium" style={{ color: '#f4f1ea' }}>{h.value}</td>
                      <td className="text-right font-semibold" style={{ color: h.pnlUp ? GREEN : RED }}>{h.pnl}</td>
                      <td className="text-right"><WalletTag label={h.wallet} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Holdings - mobile stacked cards */}
            <div className="mt-4 space-y-2 md:hidden">
              {HOLDINGS.map((h) => (
                <div
                  key={h.symbol}
                  className="rounded-lg px-3 py-2.5"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    border: '0.5px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0"
                      style={{ backgroundColor: h.color + '22', color: h.color }}
                    >
                      {h.symbol.slice(0, 2)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] font-medium truncate" style={{ color: '#f4f1ea' }}>
                        {h.name} <span style={{ color: 'rgba(244,241,234,0.4)' }}>{h.symbol}</span>
                      </div>
                      <div className="text-[10px] tabular-nums" style={{ color: 'rgba(244,241,234,0.4)' }}>
                        {h.amount} · {h.current}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[12px] font-semibold tabular-nums" style={{ color: '#f4f1ea' }}>
                        {h.value}
                      </div>
                      <div className="text-[10px] tabular-nums" style={{ color: h.pnlUp ? GREEN : RED }}>
                        {h.pnl} · <span style={{ color: h.chgUp ? GREEN : RED }}>{h.chg}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <WalletTag label={h.wallet} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
